import React from "react";
import { Link } from "react-router-dom";
import type { UseQueryResult } from "@tanstack/react-query";
import {
  Activity,
  Flag,
  MessagesSquare,
  Scale,
} from "lucide-react";
import type { CommunityGovernance } from "lib/api/community";
import {
  formatCommunityFeedbackStatus,
  type CommunityUiStrings,
} from "i18n/communityUi";
import SectionShell from "../SectionShell";
import {
  SectionEmptyState,
  SectionErrorState,
  SectionLoadingState,
} from "../SectionStates";
import { resolveCommunityTeabates } from "../utils";

interface GovernanceHealthProps {
  query: UseQueryResult<CommunityGovernance>;
  labels: CommunityUiStrings;
  numberFormatter: Intl.NumberFormat;
}

/**
 * Governance dashboard quadrant showing feedback, teabate, sentiment, and report health.
 */
const GovernanceHealth: React.FC<GovernanceHealthProps> = ({
  query,
  labels,
  numberFormatter,
}) => {
  return (
    <SectionShell
      title={labels.governanceTitle}
      subtitle={labels.governanceSubtitle}
      actions={
        <span className="inline-flex rounded-full border border-eoe-border/80 bg-eoe-bg-hover/60 px-3 py-1 text-xs font-medium text-eoe-text dark:border-eoe-dark-border/80 dark:bg-eoe-dark-bg-hover/30 dark:text-eoe-dark-text">
          {labels.governanceScoreLabel}:{" "}
          {query.data ? `${query.data.governance_score.toFixed(1)}%` : "0%"}
        </span>
      }
      contentClassName="space-y-4"
    >
      {query.isPending ? (
        <SectionLoadingState message={labels.loading} />
      ) : query.isError ? (
        <SectionErrorState
          message={labels.sectionError}
          retryLabel={labels.retry}
          onRetry={() => {
            void query.refetch();
          }}
          isRetrying={query.isRefetching}
        />
      ) : query.data ? (
        (() => {
          const teabates = resolveCommunityTeabates(query.data);
          const totalFeedback = query.data.feedback.pipeline.reduce((sum, bucket) => sum + bucket.count, 0);
          const totalSentimentResponses = query.data.sentiment.total_responses;
          const hasData =
            totalFeedback > 0
            || teabates.total_active_teabates > 0
            || query.data.reports.total_in_window > 0
            || totalSentimentResponses > 0;

          if (!hasData) {
            return <SectionEmptyState title={labels.emptyGovernance} />;
          }

          return (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              <div className="rounded-2xl border border-eoe-border/80 bg-eoe-bg-hover/30 p-4 dark:border-eoe-dark-border/80 dark:bg-eoe-dark-bg-hover/15">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                      {labels.feedbackPipelineTitle}
                    </p>
                    <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                      {labels.feedbackPipelineSubtitle}
                    </p>
                  </div>
                  <MessagesSquare className="size-5 text-eoe-text-sub dark:text-eoe-dark-text-sub" aria-hidden />
                </div>
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div>
                      <p className="text-eoe-text-sub dark:text-eoe-dark-text-sub">{labels.feedbackOpen}</p>
                      <p className="text-2xl font-semibold text-eoe-text dark:text-eoe-dark-text">
                        {numberFormatter.format(query.data.feedback.total_open)}
                      </p>
                    </div>
                    <div>
                      <p className="text-eoe-text-sub dark:text-eoe-dark-text-sub">{labels.feedbackNewInWindow}</p>
                      <p className="text-2xl font-semibold text-eoe-text dark:text-eoe-dark-text">
                        {numberFormatter.format(query.data.feedback.new_in_window)}
                      </p>
                    </div>
                    <div>
                      <p className="text-eoe-text-sub dark:text-eoe-dark-text-sub">{labels.feedbackResponseRate}</p>
                      <p className="text-2xl font-semibold text-eoe-text dark:text-eoe-dark-text">
                        {query.data.feedback.response_rate_pct.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                  <div className="overflow-hidden rounded-full border border-eoe-border/70 dark:border-eoe-dark-border/70">
                    <div className="flex h-4 items-stretch">
                      {query.data.feedback.pipeline.map((bucket, index) => {
                        const width = totalFeedback > 0 ? (bucket.count / totalFeedback) * 100 : 0;
                        const palette = [
                          "bg-blue-500",
                          "bg-cyan-500",
                          "bg-violet-500",
                          "bg-slate-400",
                          "bg-amber-500",
                          "bg-emerald-500",
                          "bg-rose-500",
                          "bg-zinc-500",
                        ];

                        return (
                          <div
                            key={bucket.status}
                            className={palette[index % palette.length]}
                            style={{ width: `${width}%` }}
                            title={`${formatCommunityFeedbackStatus(bucket.status, labels)}: ${bucket.count}`}
                          />
                        );
                      })}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                    {query.data.feedback.pipeline.map((bucket) => (
                      <div
                        key={bucket.status}
                        className="rounded-xl border border-eoe-border/70 bg-white/60 px-3 py-2 dark:border-eoe-dark-border/70 dark:bg-eoe-dark-bg/30"
                      >
                        <div className="font-medium text-eoe-text dark:text-eoe-dark-text">
                          {formatCommunityFeedbackStatus(bucket.status, labels)}
                        </div>
                        <div>{numberFormatter.format(bucket.count)}</div>
                      </div>
                    ))}
                  </div>
                  <Link
                    to="/user-feedback"
                    className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
                  >
                    {labels.viewFeedback}
                  </Link>
                </div>
              </div>

              <div className="rounded-2xl border border-eoe-border/80 bg-eoe-bg-hover/30 p-4 dark:border-eoe-dark-border/80 dark:bg-eoe-dark-bg-hover/15">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                      {labels.teabateParticipationTitle}
                    </p>
                    <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                      {labels.teabateParticipationSubtitle}
                    </p>
                  </div>
                  <Scale className="size-5 text-eoe-text-sub dark:text-eoe-dark-text-sub" aria-hidden />
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {[
                    {
                      label: labels.teabateActive,
                      value: teabates.total_active_teabates,
                    },
                    {
                      label: labels.teabateNew,
                      value: teabates.new_in_window,
                    },
                    {
                      label: labels.teabateAlignmentParticipants,
                      value: teabates.alignment_participants,
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="rounded-xl border border-eoe-border/70 bg-white/60 px-3 py-3 dark:border-eoe-dark-border/70 dark:bg-eoe-dark-bg/30"
                    >
                      <p className="text-sm font-medium text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        {item.label}
                      </p>
                      <p className="mt-2 text-2xl font-semibold text-eoe-text dark:text-eoe-dark-text">
                        {numberFormatter.format(item.value)}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex items-center justify-between rounded-xl border border-eoe-border/70 bg-white/60 px-3 py-3 text-sm dark:border-eoe-dark-border/70 dark:bg-eoe-dark-bg/30">
                  <span className="text-eoe-text-sub dark:text-eoe-dark-text-sub">
                    {labels.alignmentResponses}
                  </span>
                  <span className="font-semibold text-eoe-text dark:text-eoe-dark-text">
                    {numberFormatter.format(teabates.total_alignments)}
                  </span>
                </div>
                <div className="mt-4">
                  <Link
                    to="/teabates/top"
                    className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
                  >
                    {labels.viewTeabates}
                  </Link>
                </div>
              </div>

              <div className="rounded-2xl border border-eoe-border/80 bg-eoe-bg-hover/30 p-4 dark:border-eoe-dark-border/80 dark:bg-eoe-dark-bg-hover/15">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                      {labels.communitySentimentTitle}
                    </p>
                    <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                      {labels.communitySentimentSubtitle}
                    </p>
                  </div>
                  <Activity className="size-5 text-eoe-text-sub dark:text-eoe-dark-text-sub" aria-hidden />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-eoe-border/70 bg-white/60 px-3 py-3 dark:border-eoe-dark-border/70 dark:bg-eoe-dark-bg/30">
                    <p className="text-sm font-medium text-eoe-text-sub dark:text-eoe-dark-text-sub">
                      {labels.sentimentParticipants}
                    </p>
                    <p className="mt-2 text-2xl font-semibold text-eoe-text dark:text-eoe-dark-text">
                      {numberFormatter.format(query.data.sentiment.participants)}
                    </p>
                  </div>
                  <div className="rounded-xl border border-eoe-border/70 bg-white/60 px-3 py-3 dark:border-eoe-dark-border/70 dark:bg-eoe-dark-bg/30">
                    <p className="text-sm font-medium text-eoe-text-sub dark:text-eoe-dark-text-sub">
                      {labels.sentimentResponses}
                    </p>
                    <p className="mt-2 text-2xl font-semibold text-eoe-text dark:text-eoe-dark-text">
                      {numberFormatter.format(query.data.sentiment.total_responses)}
                    </p>
                  </div>
                </div>
                <div className="mt-4 space-y-3">
                  {[
                    {
                      key: "positive",
                      label: labels.sentimentPositive,
                      count: query.data.sentiment.positive,
                      className: "bg-emerald-500",
                    },
                    {
                      key: "neutral",
                      label: labels.sentimentNeutral,
                      count: query.data.sentiment.neutral,
                      className: "bg-slate-400",
                    },
                    {
                      key: "negative",
                      label: labels.sentimentNegative,
                      count: query.data.sentiment.negative,
                      className: "bg-rose-500",
                    },
                  ].map((bucket) => {
                    const width =
                      totalSentimentResponses > 0
                        ? Math.round((bucket.count / totalSentimentResponses) * 100)
                        : 0;

                    return (
                      <div key={bucket.key} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {bucket.label}
                          </span>
                          <span className="font-medium text-eoe-text dark:text-eoe-dark-text">
                            {numberFormatter.format(bucket.count)}
                          </span>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-eoe-border/70 dark:bg-eoe-dark-border/70">
                          <div className={`h-full rounded-full ${bucket.className}`} style={{ width: `${width}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4">
                  <Link
                    to="/issues/top"
                    className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
                  >
                    {labels.viewSentiment}
                  </Link>
                </div>
              </div>

              <div className="rounded-2xl border border-eoe-border/80 bg-eoe-bg-hover/30 p-4 dark:border-eoe-dark-border/80 dark:bg-eoe-dark-bg-hover/15">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                      {labels.contentHealthTitle}
                    </p>
                    <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                      {labels.contentHealthSubtitle}
                    </p>
                  </div>
                  <Flag className="size-5 text-eoe-text-sub dark:text-eoe-dark-text-sub" aria-hidden />
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  {[
                    {
                      label: labels.reportsInWindow,
                      value: query.data.reports.total_in_window,
                    },
                    {
                      label: labels.reportsPending,
                      value: query.data.reports.pending,
                    },
                    {
                      label: labels.reportsResolved,
                      value: query.data.reports.resolved,
                    },
                    {
                      label: labels.reportResolutionRate,
                      value: `${query.data.reports.resolution_rate_pct.toFixed(1)}%`,
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="rounded-xl border border-eoe-border/70 bg-white/60 px-3 py-3 dark:border-eoe-dark-border/70 dark:bg-eoe-dark-bg/30"
                    >
                      <p className="text-sm font-medium text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        {item.label}
                      </p>
                      <p className="mt-2 text-2xl font-semibold text-eoe-text dark:text-eoe-dark-text">
                        {typeof item.value === "string" ? item.value : numberFormatter.format(item.value)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })()
      ) : (
        <SectionLoadingState message={labels.loading} />
      )}
    </SectionShell>
  );
};

export default GovernanceHealth;

